import { useEffect } from "react";
import {
  useArchiveGoal,
  useCreateGoal,
  useGoals,
  type GoalWithStats,
} from "./api";

/**
 * Daily goals every member has. Each check-in on one needs a photo, and some
 * also need a written line (`noteRequired`).
 *
 * They are recognised by title rather than a column, so this needs no schema
 * change. The requirement is enforced in the UI only.
 */
export interface RequiredGoal {
  title: string;
  description: string;
  /** Whether the check-in needs a written note as well as the photo. */
  noteRequired: boolean;
  /** Placeholder for the note field in the check-in sheet. */
  prompt: string;
  /** One-liner on the profile card while today is still open. */
  cardLine: string;
}

export const REQUIRED_GOALS: RequiredGoal[] = [
  {
    title: "Gratitude",
    description: "Write what you're grateful for, with a photo of it.",
    noteRequired: true,
    prompt: "what are you grateful for today?",
    cardLine: "something you're grateful for, with a photo",
  },
  {
    title: "Make your bed",
    description: "Make your bed every day, with a photo.",
    noteRequired: false,
    prompt: "anything to add?",
    cardLine: "a photo of the made bed",
  },
  {
    title: "Skincare AM",
    description: "Morning skincare routine, with a photo.",
    noteRequired: false,
    prompt: "anything to add?",
    cardLine: "a photo of your morning routine",
  },
  {
    title: "Move your body",
    description: "Move your body every day. Say what you did, with a photo.",
    noteRequired: true,
    prompt: "what did you do to move your body?",
    cardLine: "what you did to move, with a photo",
  },
  {
    title: "Stretch",
    description: "Stretch every day, with a photo.",
    noteRequired: false,
    prompt: "anything to add?",
    cardLine: "a photo of your stretch",
  },
  {
    title: "Journal",
    description: "Journal every day, with a photo.",
    noteRequired: false,
    prompt: "anything to add?",
    cardLine: "a photo of your journal",
  },
  {
    title: "Skincare PM",
    description: "Evening skincare routine, with a photo.",
    noteRequired: false,
    prompt: "anything to add?",
    cardLine: "a photo of your evening routine",
  },
];

const sameTitle = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/** The required-goal definition a goal matches, if any. */
export function requiredGoalFor(
  goal: Pick<GoalWithStats, "title">,
): RequiredGoal | undefined {
  return REQUIRED_GOALS.find((required) =>
    sameTitle(required.title, goal.title),
  );
}

/**
 * Goals created (or being created) this session, per user and title. Module-level on purpose:
 * a ref is per mount, and two effect runs 20ms apart (StrictMode, a remount
 * before the goals refetch lands) each saw "missing" and both inserted.
 */
const inFlight = new Set<string>();

/**
 * Make sure the signed-in user has every required goal active, creating (or
 * un-archiving) any that are missing. Safe to mount anywhere: it waits for goals
 * to load and runs at most once per mount.
 */
export function useEnsureRequiredGoals(userId: string | null | undefined) {
  const goals = useGoals();
  const create = useCreateGoal(userId ?? "");
  const archive = useArchiveGoal();

  useEffect(() => {
    if (!userId || !goals.data) return;

    for (const required of REQUIRED_GOALS) {
      const mine = goals.data.filter(
        (goal) =>
          goal.owner_id === userId && sameTitle(goal.title, required.title),
      );
      if (mine.some((goal) => !goal.is_archived)) continue;

      const key = `${userId}|${required.title}`;
      if (inFlight.has(key)) continue;
      inFlight.add(key);
      // Cleared only on failure so a retry is possible. On success the key stays
      // put, since goals.data can still be stale for a render or two.
      const failed = () => inFlight.delete(key);

      if (mine[0]) {
        archive.mutate(
          { id: mine[0].id, archived: false },
          { onError: failed },
        );
      } else {
        create.mutate(
          {
            title: required.title,
            description: required.description,
            cadence: "daily",
            target_per_period: 1,
            photo_required: true,
          },
          { onError: failed },
        );
      }
    }
  }, [userId, goals.data, create, archive]);
}

/**
 * A pill colour for a goal, taken from along the blue-to-green gradient of the
 * Pact accent (hue 240 to 155). The required goals are spread evenly across
 * it in order; any other goal is placed by a hash of its title.
 */
export function goalPillColor(title: string): { background: string; borderColor: string } {
  const index = REQUIRED_GOALS.findIndex((required) => sameTitle(required.title, title));
  let t: number;
  if (index >= 0) {
    t = REQUIRED_GOALS.length > 1 ? index / (REQUIRED_GOALS.length - 1) : 0;
  } else {
    let hash = 0;
    for (const char of title.trim().toLowerCase()) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
    t = (hash % 100) / 99;
  }
  const hue = 240 - 85 * t;
  return {
    background: `oklch(0.93 0.05 ${hue.toFixed(0)})`,
    borderColor: `oklch(0.84 0.08 ${hue.toFixed(0)})`,
  };
}
